import { Component, Input, OnInit } from '@angular/core';
import { ProcessSpecificMetrics } from '../../services/dashboard.service';

@Component({
  selector: 'app-process-dashboard',
  templateUrl: './process-dashboard.component.html',
  styleUrls: ['./process-dashboard.component.css']
})
export class ProcessDashboardComponent implements OnInit {

  @Input() processMetrics!: ProcessSpecificMetrics;

  // Configuración de gráficos optimizada
  public progressChartOptions: any = {};
  public tasksChartOptions: any = {};

  ngOnInit(): void {
    if (this.processMetrics) {
      this.initializeCharts();
    }
  }

  ngOnChanges(): void {
    if (this.processMetrics) {
      this.initializeCharts();
    }
  }

  private initializeCharts(): void {
    this.setupProgressChart();
    this.setupTasksChart();
  }

  private setupProgressChart(): void {
    const progress = this.processMetrics.ProgressPercentage || 0;
    
    this.progressChartOptions = {
      series: [progress],
      chart: {
        type: 'radialBar',
        height: 180,
        sparkline: {
          enabled: true
        }
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          track: {
            background: "#e7e7e7",
            strokeWidth: '97%',
            margin: 5
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              offsetY: -10,
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#323130',
              formatter: function (val: number) {
                return val.toFixed(1) + "%";
              }
            }
          },
          hollow: {
            size: '70%'
          }
        }
      },
      colors: [this.getProgressColor(progress)],
      stroke: {
        lineCap: 'round'
      }
    };
  }

  private setupTasksChart(): void {
    const completed = this.processMetrics.CompletedTasks || 0;
    const pending = this.processMetrics.PendingTasks || 0;
    const failed = this.processMetrics.FailedTasks || 0;
    
    // Solo mostrar categorías que tienen datos
    const categories: string[] = [];
    const data: number[] = [];
    const colors: string[] = [];
    
    if (completed > 0) {
      categories.push('Completadas');
      data.push(completed);
      colors.push('#107c10');
    }
    
    if (pending > 0) {
      categories.push('Pendientes');
      data.push(pending);
      colors.push('#ff8c00');
    }
    
    if (failed > 0) {
      categories.push('Fallidas');
      data.push(failed);
      colors.push('#d13438');
    }

    // Si no hay datos, mostrar mensaje
    if (data.length === 0) {
      categories.push('Sin Tareas');
      data.push(0);
      colors.push('#e1dfdd');
    }

    this.tasksChartOptions = {
      series: [{
        name: 'Tareas',
        data: data
      }],
      chart: {
        type: 'bar',
        height: 180,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: false,
          distributed: true,
          dataLabels: {
            position: 'top'
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val: number) {
          return val > 0 ? val.toString() : '';
        },
        offsetY: -20,
        style: {
          fontSize: '11px',
          colors: ["#304758"],
          fontWeight: 'bold'
        }
      },
      colors: colors,
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        labels: {
          show: false
        }
      },
      grid: {
        show: false
      },
      legend: {
        show: false
      }
    };
  }

  private getProgressColor(progress: number): string {
    if (progress >= 90) return '#107c10'; // Verde para casi completado
    if (progress >= 50) return '#0078d4'; // Azul para en progreso bueno
    if (progress >= 25) return '#ff8c00'; // Naranja para progreso medio
    return '#d13438'; // Rojo para poco progreso
  }

  getStatusClass(): string {
    const status = this.processMetrics.ProcessStatus?.toLowerCase() || '';
    if (status.includes('completado') || status.includes('completed')) return 'status-completed';
    if (status.includes('ejecutando') || status.includes('running')) return 'status-running';
    if (status.includes('pausado') || status.includes('paused')) return 'status-paused';
    if (status.includes('error') || status.includes('fallido')) return 'status-error';
    return 'status-running';
  }

  getStatusDisplayText(): string {
    const status = this.processMetrics.ProcessStatus || 'Desconocido';
    
    // Mapear estados comunes a textos más amigables
    const statusMap: { [key: string]: string } = {
      'Ejecutando': '🔄 En Ejecución',
      'Completado': '✅ Completado',
      'Pausado': '⏸️ Pausado',
      'Error': '❌ Error',
      'Fallido': '❌ Fallido',
      'Pendiente': '⏳ Pendiente',
      'Cancelado': '🚫 Cancelado',
      'Desconocido': '❓ Desconocido'
    };

    // Buscar coincidencia exacta primero
    if (statusMap[status]) {
      return statusMap[status];
    }

    // Si no hay coincidencia exacta, buscar por palabras clave
    const statusLower = status.toLowerCase();
    if (statusLower.includes('ejecut') || statusLower.includes('running')) {
      return '🔄 En Ejecución';
    }
    if (statusLower.includes('complet') || statusLower.includes('finaliz')) {
      return '✅ Completado';
    }
    if (statusLower.includes('paus') || statusLower.includes('suspend')) {
      return '⏸️ Pausado';
    }
    if (statusLower.includes('error') || statusLower.includes('fall')) {
      return '❌ Error';
    }
    if (statusLower.includes('cancel')) {
      return '🚫 Cancelado';
    }
    if (statusLower.includes('pendiente') || statusLower.includes('espera')) {
      return '⏳ Pendiente';
    }

    // Si no se encuentra ninguna coincidencia, mostrar el estado original con icono genérico
    return `📋 ${status}`;
  }

  getExecutionTimeDisplay(): string {
    const hours = this.processMetrics.ExecutionTimeHours || 0;
    
    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes}min`;
    } else if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.round(hours % 24);
      return `${days}d ${remainingHours}h`;
    }
  }

  getTotalTasksDisplay(): string {
    const total = this.processMetrics.TotalTasks || 0;
    const completed = this.processMetrics.CompletedTasks || 0;
    return `${completed}/${total}`;
  }

  // Determinar si mostrar gráficos basado en si hay datos útiles
  shouldShowProgressChart(): boolean {
    return (this.processMetrics.ProgressPercentage || 0) > 0;
  }

  shouldShowTasksChart(): boolean {
    const total = (this.processMetrics.CompletedTasks || 0) + 
                  (this.processMetrics.PendingTasks || 0) + 
                  (this.processMetrics.FailedTasks || 0);
    return total > 0;
  }

  // Método para formatear fechas
  formatDate(dateString: string): string {
    if (!dateString) return 'No disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Fecha inválida';
    }
  }

  // Método para obtener información resumida del proceso
  getProcessSummary(): string {
    const completed = this.processMetrics.CompletedTasks || 0;
    const total = this.processMetrics.TotalTasks || 0;
    const progress = this.processMetrics.ProgressPercentage || 0;
    
    if (total === 0) {
      return 'Proceso sin tareas definidas';
    }
    
    if (progress >= 100) {
      return 'Proceso completado exitosamente';
    }
    
    if (completed === 0) {
      return 'Proceso iniciado, sin tareas completadas aún';
    }

    return `${completed} de ${total} tareas por completar (${progress.toFixed(1)}%)`;
  }

  // Método para obtener consejos sobre el proceso
  getProcessInsight(): string {
    const progress = this.processMetrics.ProgressPercentage || 0;
    const failed = this.processMetrics.FailedTasks || 0;
    const status = this.processMetrics.ProcessStatus?.toLowerCase() || '';
    const executionTime = this.processMetrics.ExecutionTimeHours || 0;
    const totalTasks = this.processMetrics.TotalTasks || 0;
    const completedTasks = this.processMetrics.CompletedTasks || 0;
    const pendingTasks = this.processMetrics.PendingTasks || 0;
    
    // Si hay tareas fallidas, priorizar esta información
    if (failed > 0) {
      return `⚠️ ${failed} tarea${failed > 1 ? 's' : ''} fallida${failed > 1 ? 's' : ''} requiere${failed > 1 ? 'n' : ''} atención`;
    }
    
    // Si el proceso está completado
    if (status.includes('completado') || status.includes('completed') || progress === 100) {
      if (executionTime > 0) {
        return `✅ Completado en ${executionTime.toFixed(1)} hora${executionTime !== 1 ? 's' : ''}`;
      }
      return '✅ Proceso completado exitosamente';
    }
    
    // Si hay tareas pendientes significativas
    if (pendingTasks > 0 && progress < 100) {
      if (progress > 80) {
        return `🎯 ${pendingTasks} tarea${pendingTasks > 1 ? 's' : ''} pendiente${pendingTasks > 1 ? 's' : ''} para finalizar`;
      }
      if (progress > 50) {
        return `📊 Avance del ${progress.toFixed(0)}% - ${pendingTasks} tarea${pendingTasks > 1 ? 's' : ''} restante${pendingTasks > 1 ? 's' : ''}`;
      }
    }
    
    // Si está en etapa inicial
    if (progress < 25) {
      if (totalTasks > 0) {
        return `🚀 Iniciado - ${totalTasks} tarea${totalTasks > 1 ? 's' : ''} programada${totalTasks > 1 ? 's' : ''}`;
      }
      return '🚀 Proceso en etapa inicial';
    }
    
    // Estado por defecto con información útil
    if (executionTime > 0) {
      return `⏱️ En ejecución durante ${executionTime.toFixed(1)} hora${executionTime !== 1 ? 's' : ''}`;
    }
    
    return '✅ Proceso en marcha normal';
  }
}
